import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  LayoutDashboard, ListChecks, Boxes, Share2, LogOut, Download, Leaf,
  ShieldAlert, Link2Off, Stethoscope, TrendingUp, DownloadCloud, RefreshCw, FileCode, Map, Trash2,
  Bell, Clock, CheckCheck, Plus, PenLine, XCircle,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
} from "recharts";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ACTION_COLORS = { "RETAIN": "#1B4332", "301 REDIRECT": "#748E63", "410 GONE": "#C05746", "NOINDEX": "#B08968", "MANUAL REVIEW": "#D97706" };
const STATUS_STYLE = {
  approved: "bg-brand/10 text-brand", passed: "bg-brand/10 text-brand",
  pending: "bg-amber-100 text-amber-700", blocked: "bg-red-100 text-red-700",
  quarantined: "bg-red-100 text-red-700", rejected: "bg-red-100 text-red-700",
  clean: "bg-brand/10 text-brand", suspicious: "bg-red-100 text-red-700",
};

function Badge({ value }) {
  return <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_STYLE[value] || "bg-brand-mist text-brand-muted"}`}>{value}</span>;
}

function exportCSV(rows, name) {
  if (!rows?.length) return;
  const headers = Object.keys(rows[0]);
  const csv = [
    headers.join(","),
    ...rows.map((r) => headers.map((h) => `"${String(r[h] ?? "").replace(/"/g, '""')}"`).join(",")),
  ].join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `${name}.csv`;
  a.click();
}

const TABS = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "import", label: "Ghost Import", icon: DownloadCloud },
  { id: "refresh", label: "Content Refresh", icon: Bell },
  { id: "urls", label: "Legacy URL Register", icon: ListChecks },
  { id: "staging", label: "Staging Review", icon: Boxes },
  { id: "distribution", label: "Distribution", icon: Share2 },
  { id: "exports", label: "Redirects & Sitemap", icon: FileCode },
];

export default function Dashboard() {
  const [tab, setTab] = useState("overview");
  const [showHistory, setShowHistory] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: summary } = useQuery({ queryKey: ["summary"], queryFn: () => api.get("/admin/reports/summary").then((r) => r.data) });
  const { data: urls = [] } = useQuery({ queryKey: ["legacy"], queryFn: () => api.get("/admin/legacy-urls").then((r) => r.data) });
  const { data: staging = [] } = useQuery({ queryKey: ["staging"], queryFn: () => api.get("/admin/staging").then((r) => r.data) });
  const { data: dist = [] } = useQuery({ queryKey: ["dist"], queryFn: () => api.get("/admin/distribution-map").then((r) => r.data) });
  const { data: importStatus } = useQuery({
    queryKey: ["import-status"],
    queryFn: () => api.get("/admin/import/status").then((r) => r.data),
    refetchInterval: (q) => (q.state.data?.state === "running" ? 2500 : false),
  });
  const { data: articles = [] } = useQuery({ queryKey: ["pub-articles"], queryFn: () => api.get("/articles").then((r) => r.data) });
  const { data: events = [] } = useQuery({ queryKey: ["dist-events"], queryFn: () => api.get("/admin/distribution-events").then((r) => r.data) });

  const importMut = useMutation({
    mutationFn: () => api.post("/admin/import/scrape").then((r) => r.data),
    onSuccess: () => {
      toast.success("Live scrape started — importing from ayurvedam.com");
      qc.invalidateQueries({ queryKey: ["import-status"] });
    },
    onError: () => toast.error("Could not start import"),
  });

  const distributeMut = useMutation({
    mutationFn: ({ slug, sites }) => api.post("/admin/distribute", { slug, sites }).then((r) => r.data),
    onSuccess: (d) => {
      toast.success(`Distributed "${d.slug}" to ${d.distributed.length} site(s)`);
      qc.invalidateQueries({ queryKey: ["dist-events"] });
      qc.invalidateQueries({ queryKey: ["dist"] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Distribution failed"),
  });

  const withdrawMut = useMutation({
    mutationFn: ({ slug, site }) => api.post("/admin/distribute/withdraw", { slug, site }).then((r) => r.data),
    onSuccess: () => {
      toast.success("Withdrawn from network");
      qc.invalidateQueries({ queryKey: ["dist-events"] });
    },
  });

  const download = async (path, filename) => {
    try {
      const res = await api.get(path, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      a.download = filename;
      a.click();
      toast.success(`Downloaded ${filename}`);
    } catch {
      toast.error("Export failed");
    }
  };

  const { data: refresh } = useQuery({
    queryKey: ["refresh"],
    queryFn: () => api.get("/admin/refresh/status").then((r) => r.data),
    refetchInterval: (q) => (q.state.data?.status?.state === "running" ? 2500 : 30000),
  });
  const { data: changes = [] } = useQuery({
    queryKey: ["changes"],
    queryFn: () => api.get("/admin/content-changes").then((r) => r.data),
    refetchInterval: refresh?.status?.state === "running" ? 2500 : 30000,
  });
  const refreshMut = useMutation({
    mutationFn: () => api.post("/admin/refresh/run").then((r) => r.data),
    onSuccess: () => {
      toast.success("Content check started");
      qc.invalidateQueries({ queryKey: ["refresh"] });
    },
    onError: () => toast.error("Could not start refresh"),
  });
  const ackMut = useMutation({
    mutationFn: (id) => api.post(`/admin/content-changes/${id}/ack`).then((r) => r.data),
    onSuccess: () => {
      toast.success("Change acknowledged");
      qc.invalidateQueries({ queryKey: ["changes"] });
      qc.invalidateQueries({ queryKey: ["refresh"] });
    },
  });
  const settingsMut = useMutation({
    mutationFn: (payload) => api.post("/admin/refresh/settings", payload).then((r) => r.data),
    onSuccess: (d) => {
      toast.success(`Schedule ${d.enabled ? "enabled" : "paused"} · every ${d.interval_hours}h`);
      qc.invalidateQueries({ queryKey: ["refresh"] });
    },
  });

  const actionMut = useMutation({
    mutationFn: ({ id, action }) => api.post(`/admin/staging/${id}/action`, { action }).then((r) => r.data),
    onSuccess: (d) => {
      toast.success(`Record ${d.migration_id} → ${d.review_status}`);
      qc.invalidateQueries({ queryKey: ["staging"] });
      qc.invalidateQueries({ queryKey: ["summary"] });
    },
    onError: () => toast.error("Action failed"),
  });

  const doLogout = async () => {
    await logout();
    navigate("/");
  };

  const urlChart = summary ? Object.entries(summary.url_actions).map(([k, v]) => ({ name: k, value: v })) : [];

  return (
    <div className="min-h-screen bg-brand-mist/40">
      {/* Top bar */}
      <div className="flex items-center justify-between border-b border-brand-line bg-white px-5 py-3.5">
        <div className="flex items-center gap-2">
          <span className="grid h-8 w-8 place-items-center rounded-full bg-brand text-brand-sand"><Leaf className="h-4 w-4" /></span>
          <span className="font-heading text-lg text-brand-ink">Migration Command Center</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="hidden text-sm text-brand-muted sm:inline">{user?.email}</span>
          <button data-testid="logout-btn" onClick={doLogout} className="inline-flex items-center gap-1.5 rounded-full border border-brand-line px-3.5 py-1.5 text-sm text-brand-muted hover:border-brand hover:text-brand">
            <LogOut className="h-4 w-4" /> Logout
          </button>
        </div>
      </div>

      <div className="mx-auto flex max-w-7xl flex-col gap-6 px-4 py-6 lg:flex-row">
        {/* Sidebar */}
        <aside className="lg:w-56 shrink-0">
          <nav className="flex gap-2 overflow-x-auto lg:flex-col">
            {TABS.map((t) => (
              <button
                key={t.id}
                data-testid={`tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`flex items-center gap-2.5 whitespace-nowrap rounded-lg px-3.5 py-2.5 text-sm font-medium transition-colors ${
                  tab === t.id ? "bg-brand text-brand-sand" : "text-brand-muted hover:bg-white"
                }`}
              >
                <t.icon className="h-4 w-4" /> {t.label}
              </button>
            ))}
          </nav>
        </aside>

        {/* Content */}
        <main className="min-w-0 flex-1">
          {tab === "overview" && summary && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
                {[
                  { label: "Legacy URLs", value: summary.counts.legacy_urls, icon: ListChecks },
                  { label: "Migrated articles", value: summary.counts.articles, icon: Boxes },
                  { label: "Organic traffic", value: summary.total_organic_traffic.toLocaleString(), icon: TrendingUp },
                  { label: "Backlinks", value: summary.total_backlinks, icon: Share2 },
                ].map((c) => (
                  <div key={c.label} data-testid={`stat-${c.label}`} className="rounded-xl border border-brand-line bg-white p-4">
                    <c.icon className="h-5 w-5 text-brand-sage" />
                    <p className="mt-3 font-heading text-2xl text-brand-ink">{c.value}</p>
                    <p className="text-xs text-brand-muted">{c.label}</p>
                  </div>
                ))}
              </div>

              <div className="grid gap-4 lg:grid-cols-2">
                <div className="rounded-xl border border-brand-line bg-white p-5">
                  <h3 className="font-heading text-lg text-brand-ink">URL decision breakdown</h3>
                  <div className="mt-4 h-56">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={urlChart} layout="vertical" margin={{ left: 20 }}>
                        <XAxis type="number" hide />
                        <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Bar dataKey="value" radius={[0, 6, 6, 0]}>
                          {urlChart.map((e, i) => <Cell key={i} fill={ACTION_COLORS[e.name] || "#748E63"} />)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div className="space-y-4">
                  <ReportCard icon={ShieldAlert} title="Suspicious content" count={summary.suspicious.length} tone="red" items={summary.suspicious.map((s) => s.title)} />
                  <ReportCard icon={Stethoscope} title="Medical review queue" count={summary.medical_review_queue.length} tone="amber" items={summary.medical_review_queue.map((s) => s.title)} />
                  <ReportCard icon={Link2Off} title="Broken URLs (410 candidates)" count={summary.broken_links.length} tone="red" items={summary.broken_links.map((s) => s.legacy_url)} />
                </div>
              </div>
            </div>
          )}

          {tab === "import" && (
            <div className="space-y-5">
              <div className="rounded-xl border border-brand-line bg-white p-6">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="max-w-lg">
                    <h3 className="font-heading text-xl text-brand-ink">Import from live Ghost site</h3>
                    <p className="mt-2 text-sm text-brand-muted">
                      Non-destructive read-only scrape of <span className="font-medium text-brand-ink">ayurvedam.com</span> via
                      sitemap + crawl. Populates the Legacy URL Register and Staging with real posts, pages, tags and
                      authors. HTML is sanitized and high-risk health content is auto-flagged for review.
                    </p>
                  </div>
                  <button
                    data-testid="run-import-btn"
                    onClick={() => importMut.mutate()}
                    disabled={importStatus?.state === "running" || importMut.isPending}
                    className="inline-flex items-center gap-2 rounded-full bg-brand px-5 py-2.5 text-sm font-medium text-brand-sand disabled:opacity-60"
                  >
                    <RefreshCw className={`h-4 w-4 ${importStatus?.state === "running" ? "animate-spin" : ""}`} />
                    {importStatus?.state === "running" ? "Importing…" : "Run live import"}
                  </button>
                </div>

                {importStatus && importStatus.state !== "idle" && (
                  <div data-testid="import-status" className="mt-6 rounded-lg border border-brand-line bg-brand-mist/40 p-4">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium text-brand-ink capitalize">State: {importStatus.state}</span>
                      <span className="text-brand-muted">{importStatus.processed || 0} / {importStatus.total || 0} pages</span>
                    </div>
                    <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-brand-line">
                      <div className="h-full bg-brand transition-all" style={{ width: `${importStatus.total ? Math.round((importStatus.processed / importStatus.total) * 100) : 0}%` }} />
                    </div>
                    {importStatus.summary && (
                      <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
                        {[
                          ["Posts", importStatus.summary.posts],
                          ["Pages", importStatus.summary.pages],
                          ["Register rows", importStatus.summary.register_rows],
                          ["Flagged high-risk", importStatus.summary.high_risk],
                        ].map(([l, v]) => (
                          <div key={l} className="rounded-lg bg-white p-3 text-center">
                            <p className="font-heading text-xl text-brand-ink">{v}</p>
                            <p className="text-xs text-brand-muted">{l}</p>
                          </div>
                        ))}
                      </div>
                    )}
                    {importStatus.error && <p className="mt-3 text-sm text-red-600">Error: {importStatus.error}</p>}
                  </div>
                )}
              </div>
            </div>
          )}

          {tab === "refresh" && (
            <div className="space-y-5">
              <div className="rounded-xl border border-brand-line bg-white p-6">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="max-w-lg">
                    <h3 className="font-heading text-xl text-brand-ink">Scheduled content refresh</h3>
                    <p className="mt-2 text-sm text-brand-muted">
                      Automatically re-scans ayurvedam.com on a schedule and flags <span className="font-medium text-brand-ink">new</span>,{" "}
                      <span className="font-medium text-brand-ink">changed</span> and <span className="font-medium text-brand-ink">removed</span> posts —
                      keeping the migration inventory in sync without overwriting reviewed content.
                    </p>
                  </div>
                  <button
                    data-testid="run-refresh-btn"
                    onClick={() => refreshMut.mutate()}
                    disabled={refresh?.status?.state === "running" || refreshMut.isPending}
                    className="inline-flex items-center gap-2 rounded-full bg-brand px-5 py-2.5 text-sm font-medium text-brand-sand disabled:opacity-60"
                  >
                    <RefreshCw className={`h-4 w-4 ${refresh?.status?.state === "running" ? "animate-spin" : ""}`} />
                    {refresh?.status?.state === "running" ? "Checking…" : "Run check now"}
                  </button>
                </div>

                <div className="mt-6 grid gap-4 sm:grid-cols-3">
                  <div className="rounded-lg border border-brand-line bg-brand-mist/40 p-4">
                    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-brand-muted">
                      <Clock className="h-3.5 w-3.5" /> Schedule
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                      <button
                        data-testid="toggle-schedule"
                        onClick={() => settingsMut.mutate({ enabled: !(refresh?.settings?.enabled ?? true), interval_hours: refresh?.settings?.interval_hours ?? 24 })}
                        className={`relative h-6 w-11 rounded-full transition-colors ${refresh?.settings?.enabled ? "bg-brand" : "bg-brand-line"}`}
                      >
                        <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${refresh?.settings?.enabled ? "left-[22px]" : "left-0.5"}`} />
                      </button>
                      <span className="text-sm text-brand-ink">{refresh?.settings?.enabled ? "Enabled" : "Paused"}</span>
                    </div>
                    <select
                      data-testid="interval-select"
                      value={refresh?.settings?.interval_hours ?? 24}
                      onChange={(e) => settingsMut.mutate({ enabled: refresh?.settings?.enabled ?? true, interval_hours: Number(e.target.value) })}
                      className="mt-3 w-full rounded-lg border border-brand-line bg-white px-2 py-1.5 text-sm outline-none"
                    >
                      <option value={1}>Every 1h</option>
                      <option value={6}>Every 6h</option>
                      <option value={12}>Every 12h</option>
                      <option value={24}>Every 24h</option>
                      <option value={72}>Every 72h</option>
                    </select>
                  </div>
                  <div className="rounded-lg border border-brand-line bg-brand-mist/40 p-4">
                    <p className="text-xs font-semibold uppercase tracking-wide text-brand-muted">Next run</p>
                    <p className="mt-2 text-sm text-brand-ink">{refresh?.next_run ? new Date(refresh.next_run).toLocaleString() : "—"}</p>
                    <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-brand-muted">Last run</p>
                    <p className="text-sm text-brand-ink">{refresh?.status?.last_run ? new Date(refresh.status.last_run).toLocaleString() : "Not run yet"}</p>
                  </div>
                  <div className="rounded-lg border border-brand-line bg-brand-mist/40 p-4">
                    <p className="text-xs font-semibold uppercase tracking-wide text-brand-muted">Last result</p>
                    {refresh?.status?.last_summary ? (
                      <div className="mt-2 space-y-1 text-sm text-brand-ink">
                        <p><span className="font-semibold text-brand-sage">{refresh.status.last_summary.new}</span> new</p>
                        <p><span className="font-semibold text-amber-600">{refresh.status.last_summary.changed}</span> changed</p>
                        <p><span className="font-semibold text-brand-clay">{refresh.status.last_summary.removed}</span> removed</p>
                        <p className="text-xs text-brand-muted">{refresh.status.last_summary.checked} pages checked</p>
                      </div>
                    ) : <p className="mt-2 text-sm text-brand-muted">—</p>}
                  </div>
                </div>
              </div>

              <div className="rounded-xl border border-brand-line bg-white">
                <div className="flex items-center justify-between border-b border-brand-line p-4">
                  <h3 className="font-heading text-lg text-brand-ink" data-testid="changes-heading">
                    Detected changes ({changes.filter((c) => c.status === "open").length} open)
                  </h3>
                  {changes.some((c) => c.status !== "open") && (
                    <button
                      data-testid="toggle-history"
                      onClick={() => setShowHistory((v) => !v)}
                      className="rounded-full border border-brand-line px-3 py-1.5 text-xs font-medium text-brand-muted hover:border-brand hover:text-brand"
                    >
                      {showHistory ? "Hide history" : "Show history"}
                    </button>
                  )}
                </div>
                {(() => {
                  const shown = showHistory ? changes : changes.filter((c) => c.status === "open");
                  if (shown.length === 0)
                    return (
                      <p className="p-8 text-center text-sm text-brand-muted" data-testid="no-changes">
                        No open changes. Your migration inventory is in sync with ayurvedam.com.
                      </p>
                    );
                  return (
                    <div className="divide-y divide-brand-line/60">
                      {shown.map((c) => {
                        const meta = {
                          new: { cls: "bg-brand/10 text-brand", Icon: Plus, label: "New post" },
                          changed: { cls: "bg-amber-100 text-amber-700", Icon: PenLine, label: "Changed" },
                          removed: { cls: "bg-red-100 text-red-700", Icon: XCircle, label: "Removed" },
                        }[c.change_type] || { cls: "bg-brand-mist text-brand-muted", Icon: Bell, label: c.change_type };
                        return (
                          <div key={c.id} data-testid={`change-${c.slug}-${c.change_type}`} className={`flex flex-wrap items-center justify-between gap-3 p-4 ${c.status !== "open" ? "opacity-50" : ""}`}>
                            <div className="min-w-0">
                              <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold ${meta.cls}`}>
                                <meta.Icon className="h-3 w-3" /> {meta.label}
                              </span>
                              <span className="ml-2 font-medium text-brand-ink">{c.title || c.slug}</span>
                              <span className="ml-2 text-xs text-brand-muted">/{c.slug} · {c.detected_at ? new Date(c.detected_at).toLocaleString() : ""}</span>
                            </div>
                            {c.status === "open" ? (
                              <button data-testid={`ack-${c.slug}-${c.change_type}`} onClick={() => ackMut.mutate(c.id)} className="inline-flex items-center gap-1 rounded-full border border-brand-line px-3 py-1.5 text-xs font-medium text-brand hover:border-brand">
                                <CheckCheck className="h-3.5 w-3.5" /> Acknowledge
                              </button>
                            ) : (
                              <span className="text-xs text-brand-muted">acknowledged</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            </div>
          )}

          {tab === "urls" && (
            <div className="rounded-xl border border-brand-line bg-white">
              <div className="flex items-center justify-between border-b border-brand-line p-4">
                <h3 className="font-heading text-lg text-brand-ink">Legacy URL Register ({urls.length})</h3>
                <button data-testid="export-urls" onClick={() => exportCSV(urls, "legacy_url_register")} className="inline-flex items-center gap-1.5 rounded-full border border-brand-line px-3 py-1.5 text-xs font-medium text-brand hover:border-brand">
                  <Download className="h-3.5 w-3.5" /> Export CSV
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-brand-line bg-brand-mist/50 text-left text-xs uppercase tracking-wide text-brand-muted">
                      <th className="p-3">ID</th><th className="p-3">Legacy URL</th><th className="p-3">Type</th>
                      <th className="p-3">Status</th><th className="p-3">Action</th><th className="p-3">Traffic</th><th className="p-3">Security</th><th className="p-3">Validation</th>
                    </tr>
                  </thead>
                  <tbody>
                    {urls.map((u) => (
                      <tr key={u.migration_id} data-testid={`url-row-${u.migration_id}`} className="border-b border-brand-line/60 hover:bg-brand-mist/30">
                        <td className="p-3 font-mono text-xs text-brand-muted">{u.migration_id}</td>
                        <td className="p-3 font-medium text-brand-ink">{u.legacy_url}{u.proposed_replacement && <span className="block text-xs text-brand-sage">→ {u.proposed_replacement}</span>}</td>
                        <td className="p-3 text-brand-muted">{u.content_type}</td>
                        <td className="p-3 text-brand-muted">{u.http_status}</td>
                        <td className="p-3"><span className="inline-block whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-semibold text-white" style={{ background: ACTION_COLORS[u.proposed_action] || "#748E63" }}>{u.proposed_action}</span></td>
                        <td className="p-3 text-brand-muted">{(u.organic_traffic || 0).toLocaleString()}</td>
                        <td className="p-3"><Badge value={u.security_status} /></td>
                        <td className="p-3"><Badge value={u.validation_status} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {tab === "staging" && (
            <div className="rounded-xl border border-brand-line bg-white">
              <div className="flex items-center justify-between border-b border-brand-line p-4">
                <h3 className="font-heading text-lg text-brand-ink">Staging Review ({staging.length})</h3>
                <button data-testid="export-staging" onClick={() => exportCSV(staging.map(({ removed_html, missing_assets, broken_links, distribution, tags, ...r }) => r), "staging_records")} className="inline-flex items-center gap-1.5 rounded-full border border-brand-line px-3 py-1.5 text-xs font-medium text-brand hover:border-brand">
                  <Download className="h-3.5 w-3.5" /> Export CSV
                </button>
              </div>
              <div className="divide-y divide-brand-line/60">
                {staging.map((s) => (
                  <div key={s.migration_id} data-testid={`staging-row-${s.migration_id}`} className="p-4">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-mono text-xs text-brand-muted">{s.migration_id}</span>
                          <Badge value={s.review_status} />
                          <span className={`rounded-full px-2.5 py-0.5 text-xs ${s.safety_class?.includes("High-risk") ? "bg-red-100 text-red-700" : "bg-brand-mist text-brand-muted"}`}>{s.safety_class}</span>
                        </div>
                        <h4 className="mt-1.5 font-heading text-lg text-brand-ink">{s.title}</h4>
                        <p className="text-xs text-brand-muted">/{s.slug} · by {s.author}{s.reviewer ? ` · reviewed by ${s.reviewer}` : ""}</p>
                        <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-xs text-brand-muted">
                          {s.removed_html?.length > 0 && <span className="text-brand-clay">⚠ {s.removed_html.length} HTML element(s) sanitized</span>}
                          {s.missing_assets?.length > 0 && <span className="text-brand-clay">⚠ {s.missing_assets.length} missing asset(s)</span>}
                          {s.broken_links?.length > 0 && <span className="text-brand-clay">⚠ {s.broken_links.length} broken link(s)</span>}
                          {s.distribution?.length > 0 && <span>→ {s.distribution.join(", ")}</span>}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button data-testid={`approve-${s.migration_id}`} onClick={() => actionMut.mutate({ id: s.migration_id, action: "approve" })} className={`rounded-full px-3 py-1.5 text-xs font-medium ${s.review_status === "approved" ? "bg-brand text-brand-sand" : "border border-brand-line text-brand-muted hover:border-brand hover:text-brand"}`}>Approve</button>
                        <button data-testid={`quarantine-${s.migration_id}`} onClick={() => actionMut.mutate({ id: s.migration_id, action: "quarantine" })} className={`rounded-full px-3 py-1.5 text-xs font-medium ${s.review_status === "quarantined" ? "bg-brand-clay text-white" : "border border-brand-clay/40 text-brand-clay hover:bg-brand-clay/5"}`}>Quarantine</button>
                        <button data-testid={`reject-${s.migration_id}`} onClick={() => actionMut.mutate({ id: s.migration_id, action: "reject" })} className={`rounded-full px-3 py-1.5 text-xs font-medium ${s.review_status === "rejected" ? "bg-brand-ink text-white" : "border border-brand-line text-brand-muted hover:border-brand-ink"}`}>Reject</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {tab === "distribution" && (
            <div className="space-y-8">
              {/* Distribute controls */}
              <div className="rounded-xl border border-brand-line bg-white p-5">
                <h3 className="font-heading text-lg text-brand-ink">Distribute reviewed content</h3>
                <p className="mt-1 text-sm text-brand-muted">Push approved article summaries to sister sites. Each card links back to the Ayurvedam.com canonical URL.</p>
                <div className="mt-4 space-y-3">
                  {articles.map((a) => (
                    <DistributeRow key={a.slug} article={a} sites={dist.map((d) => d.site)} events={events} onDistribute={(sites) => distributeMut.mutate({ slug: a.slug, sites })} pending={distributeMut.isPending} />
                  ))}
                </div>
              </div>

              {/* Active events */}
              <div className="rounded-xl border border-brand-line bg-white">
                <div className="flex items-center justify-between border-b border-brand-line p-4">
                  <h3 className="font-heading text-lg text-brand-ink">Active distribution ({events.filter((e) => e.status === "active").length})</h3>
                  <a href="/network" target="_blank" rel="noreferrer" data-testid="open-network-preview" className="inline-flex items-center gap-1.5 rounded-full border border-brand-line px-3 py-1.5 text-xs font-medium text-brand hover:border-brand">
                    <Map className="h-3.5 w-3.5" /> Open network preview
                  </a>
                </div>
                <div className="divide-y divide-brand-line/60">
                  {events.filter((e) => e.status === "active").map((e) => (
                    <div key={`${e.slug}-${e.site}`} data-testid={`event-${e.slug}-${e.site}`} className="flex items-center justify-between gap-3 p-3 text-sm">
                      <div className="min-w-0">
                        <span className="rounded-full bg-brand/10 px-2 py-0.5 text-xs font-medium text-brand">{e.site}</span>
                        <span className="ml-2 text-brand-ink">{e.title}</span>
                        <span className="ml-2 text-xs text-brand-muted">v{e.version}</span>
                      </div>
                      <button data-testid={`withdraw-${e.slug}-${e.site}`} onClick={() => withdrawMut.mutate({ slug: e.slug, site: e.site })} className="inline-flex items-center gap-1 rounded-full border border-brand-line px-2.5 py-1 text-xs text-brand-clay hover:border-brand-clay">
                        <Trash2 className="h-3.5 w-3.5" /> Withdraw
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Ownership map */}
              <div>
                <h3 className="mb-3 font-heading text-lg text-brand-ink">Sister-site distribution map</h3>
                <div className="grid gap-4 sm:grid-cols-2">
                  {dist.map((d) => (
                    <div key={d.site} data-testid={`dist-${d.site}`} className="rounded-xl border border-brand-line bg-white p-5">
                      <div className="flex items-center gap-2">
                        <Share2 className="h-4 w-4 text-brand-sage" />
                        <h4 className="font-heading text-lg text-brand-ink">{d.site}</h4>
                      </div>
                      <p className="mt-1 text-xs text-brand-clay">Owns: {d.owns}</p>
                      <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-brand-muted">Receives ({d.content_type})</p>
                      <ul className="mt-2 space-y-1 text-sm text-brand-ink/80">
                        {d.receives.map((r, i) => (
                          <li key={i} className="flex gap-2"><span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-brand-sage" />{r}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {tab === "exports" && (
            <div className="space-y-4">
              <div className="rounded-xl border border-brand-line bg-white p-6">
                <h3 className="font-heading text-xl text-brand-ink">Redirect & sitemap exports</h3>
                <p className="mt-2 text-sm text-brand-muted">
                  Generated from the Legacy URL Register. 301s reach targets in one hop; 410s mark removed content.
                  The sitemap includes only canonical RETAIN pages returning 200.
                </p>
                <div className="mt-5 grid gap-4 sm:grid-cols-3">
                  {[
                    { label: "nginx redirects", desc: "location blocks (301/410)", path: "/admin/export/nginx", file: "ayurvedam-redirects.nginx.conf", icon: FileCode },
                    { label: ".htaccess redirects", desc: "Apache Redirect rules", path: "/admin/export/htaccess", file: "ayurvedam.htaccess", icon: FileCode },
                    { label: "sitemap.xml", desc: "Canonical 200 pages only", path: "/admin/export/sitemap.xml", file: "sitemap.xml", icon: Map },
                  ].map((x) => (
                    <div key={x.label} className="rounded-xl border border-brand-line bg-brand-mist/30 p-4">
                      <x.icon className="h-5 w-5 text-brand-sage" />
                      <h4 className="mt-3 font-medium text-brand-ink">{x.label}</h4>
                      <p className="text-xs text-brand-muted">{x.desc}</p>
                      <button
                        data-testid={`export-${x.file}`}
                        onClick={() => download(x.path, x.file)}
                        className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand px-4 py-2 text-xs font-medium text-brand-sand hover:opacity-90"
                      >
                        <Download className="h-3.5 w-3.5" /> Download
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

function DistributeRow({ article, sites, events, onDistribute, pending }) {
  const distributedSites = events.filter((e) => e.slug === article.slug && e.status === "active").map((e) => e.site);
  const [selected, setSelected] = useState(distributedSites);
  useEffect(() => {
    setSelected(events.filter((e) => e.slug === article.slug && e.status === "active").map((e) => e.site));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events, article.slug]);
  const toggle = (s) => setSelected((p) => (p.includes(s) ? p.filter((x) => x !== s) : [...p, s]));
  return (
    <div data-testid={`distribute-row-${article.slug}`} className="rounded-lg border border-brand-line p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="font-medium text-brand-ink">{article.title}</p>
        <button
          data-testid={`distribute-btn-${article.slug}`}
          onClick={() => onDistribute(selected)}
          disabled={pending || selected.length === 0}
          className="rounded-full bg-brand px-3.5 py-1.5 text-xs font-medium text-brand-sand disabled:opacity-50"
        >
          Distribute ({selected.length})
        </button>
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {sites.map((s) => (
          <button
            key={s}
            data-testid={`dist-chip-${article.slug}-${s}`}
            onClick={() => toggle(s)}
            className={`rounded-full border px-2.5 py-1 text-xs transition-colors ${
              selected.includes(s) ? "border-brand bg-brand text-brand-sand" : "border-brand-line text-brand-muted hover:border-brand"
            }`}
          >
            {s}
          </button>
        ))}
      </div>
    </div>
  );
}

function ReportCard({ icon: Icon, title, count, items, tone }) {
  const toneCls = tone === "red" ? "text-red-600" : "text-amber-600";
  return (
    <div className="rounded-xl border border-brand-line bg-white p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className={`h-4 w-4 ${toneCls}`} />
          <h4 className="text-sm font-semibold text-brand-ink">{title}</h4>
        </div>
        <span className={`font-heading text-xl ${toneCls}`}>{count}</span>
      </div>
      {items?.length > 0 && (
        <ul className="mt-2 space-y-0.5 text-xs text-brand-muted">
          {items.slice(0, 3).map((it, i) => <li key={i} className="truncate">• {it}</li>)}
        </ul>
      )}
    </div>
  );
}
